import { Fragment } from 'react';
import { Icon } from 'components/UI/Icon';
import Image from 'components/UI/Image/Image';
import Link from 'components/UI/Link/Link';
import { getTeaserSizes } from 'components/UI/Teaser/Teaser';
import type { WidgetTeaserItemProps } from 'types/cms-teaser';
import { isMobile } from 'utils/detect';
import { generateWeakKey } from 'utils/idGenerator';
import { formatMillisecondsToMinutes, getPublishedLabel } from 'utils/time';
import { Button } from '../Button';
import styles from './TeaserSearch.module.scss';

export type TeaserSearchProps = {
  teaser: WidgetTeaserItemProps;
  id?: string;
};

const TeaserSearch = ({ teaser, id }: TeaserSearchProps) => {
  const image = teaser.model.image;
  const videoDuration = teaser.model.fields.encoding?.[0]?.value?.duration?.value;
  const link = teaser.type === 'rtl-plus-series' ? teaser.model.utm_URL : teaser.model.url;
  const genreLength = teaser.model.fields.genre?.length ?? 0;

  return (
    <Link id={id} className={styles.root} href={link} data-tracking-id="teaser-link">
      <div className={styles.imgWrapper}>
        <Image
          className={styles.image}
          image={image}
          width={isMobile() ? 130 : 240}
          sizes={getTeaserSizes('small')}
          cropOverride={isMobile() ? 'c3_4' : 'c16_9'}
          alt={teaser.model?.image?.fields?.alt?.value ?? `Bild zu: "${teaser.model.fields.title}"`}
        />
        {teaser.type === 'video' && videoDuration && (
          <span className={styles.videoTime}>
            <Icon className={styles.iconPlay} name="play" size={16} />
            {formatMillisecondsToMinutes(Number(videoDuration), '', false)}
          </span>
        )}
      </div>
      <div className={styles.content}>
        {teaser.type === 'rtl-plus-series' && <span>Streaming - Tipp</span>}
        <h3 className={styles.title}>{teaser.model.fields.title}</h3>
        {teaser.model?.fields?.['seo-description'] && (
          <div className={styles.seoDescription}>{teaser.model.fields['seo-description']}</div>
        )}
        <div className={styles.meta}>
          {(teaser.type === 'storyline' || teaser.type === 'video') && (
            <>
              <span>{teaser.model.lastmodifiedAt && getPublishedLabel(teaser.model.lastmodifiedAt)}</span>
              <span className={styles.separator}>•</span>
            </>
          )}
          {teaser.type === 'rtl-plus-series' ? (
            teaser.model.fields.genre?.map((item, index) => (
              <Fragment key={generateWeakKey(item, 'rtlplusgenres')}>
                <span>{item.value}</span>
                {index < genreLength - 1 && <span className={styles.separator}>•</span>}
              </Fragment>
            ))
          ) : (
            <>
              {teaser.type === 'series' && (
                <>
                  <span>Sendung</span>
                  <span className={styles.separator}>•</span>
                </>
              )}
              <span>{teaser?.model?.themenwelt?.section_name}</span>
            </>
          )}
        </div>
        {teaser.type === 'rtl-plus-series' && <Button className={styles.rtlplusbutton} variant="rtlPlus" />}
      </div>
    </Link>
  );
};

export default TeaserSearch;
